import { FC, useCallback } from 'react';
import { InlineLoading, Tag } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import 'components/inline-edit/ibm-products-config';
import { Section } from './section';
import { fetchAgentConfig } from './api';
import { useAgentResource } from './use-agent-resource';
import styles from './page.module.scss';

interface AgentConfigSidePanelProps {
  agentType: string | null;
  open: boolean;
  onClose: () => void;
}

// Long, multi-line config values (prompts, templates) read better as code blocks.
const MULTILINE_KEYS = new Set(['system_prompt', 'user_prompt_template']);

const fmt = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return String(value);
};

export const AgentConfigSidePanel: FC<AgentConfigSidePanelProps> = ({
  agentType,
  open,
  onClose,
}) => {
  const {
    data: config,
    loading,
    error,
  } = useAgentResource(
    useCallback(
      (controller: AbortController) =>
        fetchAgentConfig(agentType as string, controller),
      [agentType]
    ),
    [agentType],
    open && !!agentType
  );

  const rawEntries = Object.entries(config?.rawConfigs ?? {});
  const tools = config?.tools ?? [];

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Agent Configuration"
      subtitle={
        config ? `${config.name} · ${config.agentType}` : (agentType ?? '')
      }
      placement="right"
      size="lg"
      slideIn
      selectorPageContent="#main-content"
    >
      {loading && <InlineLoading description="Loading configuration..." />}
      {error && <div className={styles.detailError}>{error}</div>}

      {config && (
        <div className={styles.detailBody}>
          <div className={styles.summaryRow}>
            <Tag type={config.enabled ? 'green' : 'gray'} size="sm">
              {config.enabled ? 'Enabled' : 'Disabled'}
            </Tag>
            <span className={styles.summaryMeta}>{config.model}</span>
          </div>

          <Section title="Model & Limits" defaultExpanded>
            <div className={styles.tokenGrid}>
              <span>Model</span>
              <span>{config.model}</span>
              <span>Temperature</span>
              <span>{fmt(config.temperature)}</span>
              <span>Max tokens</span>
              <span>{fmt(config.maxTokens)}</span>
              <span>Max iterations</span>
              <span>{fmt(config.maxIterations)}</span>
              <span>Max suggestions</span>
              <span>{fmt(config.maxSuggestions)}</span>
              <span>Confidence threshold</span>
              <span>{fmt(config.confidenceThreshold)}</span>
            </div>
          </Section>

          <Section title="Network">
            <div className={styles.tokenGrid}>
              <span>Request timeout</span>
              <span>
                {config.requestTimeout === null
                  ? '— (client default)'
                  : `${config.requestTimeout}s`}
              </span>
              <span>Max retries</span>
              <span>
                {config.maxRetries === null
                  ? '— (client default)'
                  : config.maxRetries}
              </span>
            </div>
          </Section>

          <Section title={`Tools (${tools.length})`}>
            {tools.length === 0 ? (
              <span className={styles.muted}>No tools configured.</span>
            ) : (
              <div className={styles.tagList}>
                {tools.map((tool) => (
                  <Tag key={tool} type="cool-gray" size="sm">
                    {tool}
                  </Tag>
                ))}
              </div>
            )}
          </Section>

          <Section title="Identity">
            <div className={styles.tokenGrid}>
              <span>Agent type</span>
              <span>{config.agentType}</span>
              <span>Channel</span>
              <span>{config.channel}</span>
              <span>Service account</span>
              <span>{config.serviceAccount ?? '—'}</span>
              <span>Created</span>
              <span>
                {config.createdAt
                  ? new Date(config.createdAt).toLocaleString()
                  : '—'}
              </span>
            </div>
          </Section>

          {rawEntries.length > 0 && (
            <Section title={`Raw config (${rawEntries.length})`}>
              {rawEntries.map(([key, value]) => {
                if (MULTILINE_KEYS.has(key)) {
                  return (
                    <div key={key}>
                      <div className={styles.rawKey}>{key}</div>
                      <pre className={styles.codeBlock}>{value}</pre>
                    </div>
                  );
                }
                return (
                  <div key={key} className={styles.tokenGrid}>
                    <span>{key}</span>
                    <span>{value}</span>
                  </div>
                );
              })}
            </Section>
          )}
        </div>
      )}
      {/* Carbon SidePanel requires a non-null child even when closed/empty. */}
      {!loading && !error && !config && <span aria-hidden />}
    </SidePanel>
  );
};
